import { ItemClass } from "../../common/constants"
import { YearlyBudget } from "../../domain/budget"
import { calculateAdjustedCost, CatalogItem } from "../../domain/catalogItem"
import { defaultEstimatedItem, EstimatedItem, syncPricing } from "../../domain/workareaItem"
import { Budget } from "../Budgets/budgetModel"

export type CatalogItemPricingBreakdown = {
  unitCost: number
  adjustedCost: number
  taxable: boolean
  tax: number
  overheadPercentage: number
  overhead: number
  breakeven: number
  profitMargin: number
  profit: number
  unitPrice: number
}

export type CatalogItemFormValues = {
  id?: number
  guid?: string
  name: string
  cost: number
  taxable: boolean
  sku?: string
  units?: string
  itemClass: ItemClass
  saveToCatalog: boolean,
  categories: string[],
  tags: string[]
  overtimeFactor?: number
  laborBurden?: number
  customPrice?: number | null
  customProfitMargin?: number
  unbillablePercent?: number
  rateInstallationQuantity?: number
  ratePerUnits?: string
  rateUsageQuantity?: number
  description?: string
}

export const catalogItemToFormValues = (catalogItem: CatalogItem): CatalogItemFormValues => ({
  ...catalogItem,
  itemClass: catalogItem.class,
  saveToCatalog: true
})

type PricingBreakdownCatalogItem = Pick<CatalogItem, 'cost' | 'unbillablePercent' | 'laborBurden' | 'taxable'>
export const calculatePricingBreakdown = (item: PricingBreakdownCatalogItem, classBudget: Budget): CatalogItemPricingBreakdown => {
  const adjustedCost = calculateAdjustedCost({
    ...item,
    unbillablePercent: (item.unbillablePercent ?? 0) * 100,
    laborBurden: (item.laborBurden ?? 0) * 100,
  })

  const dummyWorkareaItem: EstimatedItem = {
    ...defaultEstimatedItem(),
    quantity: 1,
    class: classBudget.class as ItemClass,
    chargeTaxOnInvoice: item.taxable,

  }

  const yearlyBudget = {
    ...classBudget
  } as YearlyBudget
  const workareaItem = syncPricing(dummyWorkareaItem, { ...item, adjustedCost }, [yearlyBudget])
  return {
    unitCost: item.cost,
    adjustedCost,
    taxable: item.taxable,
    tax: workareaItem.tax,
    overheadPercentage: workareaItem.overheadPercentage,
    overhead: workareaItem.overhead,
    breakeven: workareaItem.breakeven,
    profitMargin: workareaItem.profitMargin,
    profit: workareaItem.profit,
    unitPrice: workareaItem.unitPrice,
  }
}

export type UpdateJobDescription = {
  id: number
  no: string
  name: string
  salesPersonName: string
  customerName: string
}

export const hasPricingChanged = (original: CatalogItem, updated: CatalogItemFormValues): boolean => {
  const pricingFields: (keyof (CatalogItem | CatalogItemFormValues))[] = [
    'cost',
    'overtimeFactor',
    'unbillablePercent',
    'laborBurden',
    'customPrice',
    'customProfitMargin',
    'taxable',
  ]

  return pricingFields.some(field => {
    const originalValue = original[field]
    const updatedValue = updated[field]

    if ((originalValue == undefined || originalValue == null) && (updatedValue == undefined || updatedValue == null)) {
      return false
    }

    return original[field] !== updated[field]
  })
}

export const normalizePercentages = (catalogItem: CatalogItem) => {
  return {
    ...catalogItem,
    unbillablePercent: catalogItem.unbillablePercent ? catalogItem.unbillablePercent / 100 : catalogItem.unbillablePercent,
    laborBurden: catalogItem.laborBurden ? catalogItem.laborBurden / 100 : catalogItem.laborBurden,
    overtimeFactor: catalogItem.overtimeFactor ? catalogItem.overtimeFactor / 100 : catalogItem.overtimeFactor,
  }
}

export const denormalizePercentages = (catalogItem: CatalogItemFormValues): CatalogItemFormValues => {
  return {
    ...catalogItem,
    unbillablePercent: catalogItem.unbillablePercent ? catalogItem.unbillablePercent * 100 : catalogItem.unbillablePercent,
    laborBurden: catalogItem.laborBurden ? catalogItem.laborBurden * 100 : catalogItem.laborBurden,
    overtimeFactor: catalogItem.overtimeFactor ? catalogItem.overtimeFactor * 100 : catalogItem.overtimeFactor,
  }
}
